import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-driven effects for the landing page (issue #80).
 *
 * One rAF-throttled listener drives every parallax element on the page rather
 * than one listener per component: layout reads are batched into a single pass,
 * so adding more parallax layers costs no extra scroll work.
 *
 * The loop only WRITES CSS custom properties; each element decides in CSS what
 * to do with them. That keeps the motion design in the stylesheet next to the
 * rest of the element's styling instead of hard-coded into JS transforms.
 *
 *   --p   0 → 1   progress as the element crosses the viewport
 *   --pc  -1 → 1  the same, centered (0 when the element is mid-viewport)
 */

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function useScrollFx() {
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    let frame = 0;
    let nodes = [];

    const collect = () => {
      nodes = Array.from(document.querySelectorAll('[data-fx]'));
    };

    // Read every rect first, then write every style. Interleaving them makes
    // each write invalidate layout for the next read, so the browser has to
    // re-layout once per element instead of once per frame.
    const update = () => {
      frame = 0;
      const vh = window.innerHeight || 1;
      const pending = [];

      for (const el of nodes) {
        const r = el.getBoundingClientRect();
        // Fully off-screen elements keep their last value; recomputing them
        // would just burn layout reads on things nobody can see.
        if (r.bottom < -vh || r.top > vh * 2) continue;
        const p = (vh - r.top) / (vh + r.height);
        pending.push([el, p < 0 ? 0 : p > 1 ? 1 : p]);
      }

      for (const [el, p] of pending) {
        el.style.setProperty('--p', p.toFixed(4));
        el.style.setProperty('--pc', (p * 2 - 1).toFixed(4));
      }
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    const recollect = () => {
      collect();
      onScroll();
    };

    collect();
    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', recollect, { passive: true });
    // Images and the display font settle after mount and change element
    // heights, so take one more reading once the page has fully loaded.
    window.addEventListener('load', recollect);

    // NOTE: do NOT re-collect from a MutationObserver on <body>. Every counter
    // tick and reveal is a DOM mutation, so an observer here fires ~180×/s and
    // each callback forces a full layout pass over every parallax node — enough
    // to lock up the main thread outright.
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', recollect);
      window.removeEventListener('load', recollect);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
}

/**
 * Reveal-on-enter. Returns a ref to attach to the element that should animate
 * in. Unobserves after firing — these are one-shot entrances, so leaving the
 * observer attached would keep re-triggering on scroll-up for no benefit.
 */
export function useReveal(options = {}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: options.rootMargin || '0px 0px -12% 0px', threshold: options.threshold ?? 0.12 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [options.rootMargin, options.threshold]);

  return [ref, shown];
}

/**
 * Counts a number up when it scrolls into view.
 *
 * Returns [wrapperRef, valueRef]. The tween writes textContent straight to the
 * value node instead of going through React state on purpose: three counters
 * re-rendering at 60fps is ~180 DOM mutations a second, which is exactly the
 * churn that makes scroll-linked layout work expensive. Nothing else on the
 * page depends on the intermediate values, so React never needs to see them.
 */
export function useCountUp(target, { duration = 1200, format } = {}) {
  const [wrapRef, shown] = useReveal();
  const valueRef = useRef(null);

  // Held in a ref so an inline arrow in the caller doesn't restart the tween
  // on every render.
  const formatRef = useRef(format);
  formatRef.current = format;

  useEffect(() => {
    const node = valueRef.current;
    if (!node) return undefined;
    const render = (v) => {
      node.textContent = formatRef.current ? formatRef.current(v) : v.toLocaleString();
    };

    if (!shown) {
      render(0);
      return undefined;
    }
    if (prefersReducedMotion()) {
      render(target);
      return undefined;
    }

    let frame = 0;
    let start = 0;
    const step = (t) => {
      if (!start) start = t;
      const progress = Math.min((t - start) / duration, 1);
      // easeOutExpo — fast commit, long settle, reads as "landing" on a number
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      render(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [shown, target, duration]);

  return [wrapRef, valueRef];
}
